import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box, Typography, Button, TextField, Spinner, Alert } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { adminApiClient, AdminApiError, WAIVER_REASONS, type RunnerApplicationDetail, type RunnerReviewBrief, type RunnerTransitionAction } from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { RunnerStageChip } from '../components/RunnerChips';
import { RunnerInsurancePanel, RunnerCallPanel } from '../components/RunnerOnboardingPanels';
import { Pill } from '../components/Pill';
import { RUNNER_PIPELINE, RUNNER_TERMINAL, runnerStageIndex, runnerStageChip, checkrStatusLabel, METRO_LABEL, TRANSPORT_LABEL } from '../lib/runnerChips';

/**
 * Runner application — review / pipeline detail (spec §5, 12 stages). Each
 * stage is one action; declines before Checkr are a plain "not a fit", declines
 * after Checkr are FCRA adverse action (their own capability). Go-live mints
 * the GLPD id and activates the Runner.
 */

const GREEN = '#2E9E3A';
// Since 2026-10-01 profile, insurance, handbook and the video call come BEFORE the check.
const POST_CHECKR = ['checkr_pending', 'checkr_clear', 'checkr_consider', 'banking_tax', 'shadow_ride', 'pre_golive_review', 'approved'];

function fmtDate(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}
function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

/** The action(s) available at each stage. */
function actionsFor(status: string): { action: RunnerTransitionAction; label: string; needs: 'review' | 'approve' | 'waive' }[] {
  switch (status) {
    case 'submitted': return [{ action: 'start_review', label: 'Start review', needs: 'review' }];
    case 'in_review': return [{ action: 'advance', label: 'Accept — on to profile and vehicle', needs: 'review' }];
    case 'profile_setup': return [{ action: 'advance', label: 'Profile and vehicle done → insurance', needs: 'review' }];
    // Approving the card (Insurance panel below) moves the application on by
    // itself; this button refuses until an approved, unexpired card is on file.
    case 'insurance_review': return [{ action: 'advance', label: 'Insurance approved → handbook', needs: 'review' }];
    case 'training_ack': return [{ action: 'advance', label: 'Handbook acknowledged → video call', needs: 'review' }];
    // 🔑 The video call is the ONLY place a check is ordered or waived. Both
    // refuse until the call is marked completed in the Video call panel.
    case 'verification_call': return [
      { action: 'proceed_to_checkr', label: 'Order background check', needs: 'review' },
      { action: 'waive_checkr', label: 'Waive background check…', needs: 'waive' },
    ];
    case 'checkr_pending': return [
      { action: 'checkr_clear', label: 'Checkr: clear', needs: 'review' },
      { action: 'checkr_consider', label: 'Checkr: consider (needs review)', needs: 'review' },
    ];
    case 'checkr_consider': return [{ action: 'checkr_clear', label: 'Resolve as clear', needs: 'review' }];
    case 'checkr_clear': return [{ action: 'advance', label: 'On to banking and tax', needs: 'review' }];
    // Same rung as a clear, so the same next step.
    case 'checkr_waived': return [{ action: 'advance', label: 'On to banking and tax', needs: 'review' }];
    case 'banking_tax': return [{ action: 'advance', label: 'Banking and tax done → final review', needs: 'review' }];
    case 'shadow_ride': return [{ action: 'advance', label: 'On to final review', needs: 'review' }];
    case 'pre_golive_review': return [{ action: 'advance', label: 'Approve for go-live', needs: 'review' }];
    case 'approved': return [{ action: 'go_live', label: 'Go live (mint GLPD, activate)', needs: 'approve' }];
    default: return [];
  }
}

export default function RunnerApplicationReview() {
  const caps = useCapabilities();
  const canView = caps.has('runner:read');
  const canPurge = caps.has('runner:purge');
  const [purgeReason, setPurgeReason] = useState('');
  const [purgeArmed, setPurgeArmed] = useState(false);
  const [purging, setPurging] = useState(false);
  const canReview = caps.has('runner:review');
  const canApprove = caps.has('runner:approve');
  // Its own grant: walking the pipeline does not by itself authorise
  // skipping a background check.
  const canWaive = caps.has('runner:checkr-waive');
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiverCode, setWaiverCode] = useState('');
  const canAdverse = caps.has('runner:adverse-action');
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const [app, setApp] = useState<RunnerApplicationDetail | null>(null);
  const [briefs, setBriefs] = useState<RunnerReviewBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.getRunnerApplication(id);
      setApp(res.application);
      setBriefs(res.briefs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (canView && id) void load();
  }, [canView, id, load]);

  const run = async (action: RunnerTransitionAction, ok: string) => {
    if (!app) return;
    setBusy(true);
    try {
      const res = await adminApiClient.transitionRunnerApplication(app.id, {
        action,
        reason: action === 'decline' ? reason.trim() : undefined,
        note: note.trim() || undefined,
        waiverCode: action === 'waive_checkr' ? waiverCode.trim() : undefined,
      });
      setFlash({ kind: 'success', msg: res.glpdId ? `${ok} Runner id ${res.glpdId}.` : ok });
      setDeclineOpen(false);
      setWaiveOpen(false);
      setWaiverCode('');
      setReason('');
      setNote('');
      await load();
    } catch (err) {
      setFlash({ kind: 'error', msg: err instanceof AdminApiError ? err.message : 'Action failed' });
    } finally {
      setBusy(false);
    }
  };

  if (caps.loading || loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view Runner applications.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!app) return <Alert severity="warning">Application not found.</Alert>;

  const name = `${app.firstName} ${app.lastName}`;
  const isTerminal = RUNNER_TERMINAL.includes(app.status);
  // 🛑 The waiver outranks the stage, exactly as runner-api and admin-api do.
  // A waived applicant reaches banking_tax and every stage after, all of
  // which are in POST_CHECKR — but no consumer report exists for them, so
  // declining is not adverse action. Labelling it so here would tell the
  // reviewer they are about to start an FCRA process that will not happen.
  const waived = app.checkrWaiverId !== null;
  const postCheckr = !waived && POST_CHECKR.includes(app.status);
  const canDecline = canReview && !isTerminal && (!postCheckr || canAdverse);
  const actions = actionsFor(app.status).filter((a) =>
    a.needs === 'approve' ? canApprove : a.needs === 'waive' ? canWaive : canReview,
  );
  const idx = runnerStageIndex(app.status);

  return (
    <Box>
      <Typography onClick={() => navigate('/runner-applications')} sx={{ color: '#1f7a3d', fontWeight: 600, fontSize: 13, cursor: 'pointer', mb: 1.5 }}>
        ‹ Runner applications
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1f2937', mb: 0.5 }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            <RunnerStageChip status={app.status} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {METRO_LABEL[app.metro] ?? app.metro} · {TRANSPORT_LABEL[app.transportMode] ?? app.transportMode}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9aa3ab' }}>
              · Applied {fmtDate(app.submittedAt)}
            </Typography>
            {app.recruitCode && <Pill label={`Recruited · ${app.recruitCode.code}`} fg="#7c3aed" bg="#ede9fe" />}
            {/* Follows the applicant for the rest of the pipeline, not just at
                the waived stage — by banking_tax the status no longer says it. */}
            {waived && <Pill label="CHECK WAIVED" fg="#7A4A08" bg="#FDF3E1" />}
            {app.profile?.glpdId && <Pill label={app.profile.glpdId} fg="#ffffff" bg={GREEN} strong />}
          </Box>
        </Box>
        {canDecline && (
          <Button ariaLabel="Decline application" buttonText={postCheckr ? 'Decline (adverse action)' : 'Decline'} variant="outlined" size="small" sx={DANGER_BTN} disabled={busy} onClick={() => setDeclineOpen((v) => !v)} />
        )}
      </Box>

      {!canReview && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">View-only access — you can browse but can’t make changes.</Alert>
        </Box>
      )}
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}

      {canDecline && declineOpen && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e6b0aa', borderRadius: 2, backgroundColor: '#fdf2f1' }}>
          <Typography sx={{ fontWeight: 700, color: '#C0392B', mb: 0.5 }}>
            {postCheckr ? 'Decline after Checkr — FCRA adverse action' : 'Decline at pre-screen'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {postCheckr
              ? 'This starts the pre-adverse notice clock (≥5 business days before final adverse action, with the report copy + summary of rights).'
              : 'No consumer report has been pulled yet — this is a plain “not a fit”.'}
          </Typography>
          <TextField label="Reason (internal)" ariaLabel="Decline reason" value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} fullWidth multiline />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button ariaLabel="Cancel decline" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} disabled={busy} onClick={() => setDeclineOpen(false)} />
            <Button ariaLabel="Confirm decline" buttonText="Confirm decline" variant="contained" size="small" disabled={busy || !reason.trim()} onClick={() => void run('decline', 'Application declined.')} />
          </Box>
        </Box>
      )}

      {/* Stepper */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
        {RUNNER_PIPELINE.map((s, i) => {
          const done = idx > i || app.status === 'live';
          const current = idx === i && !isTerminal;
          return (
            <Box key={s.status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Pill label={s.short} fg={done ? '#fff' : current ? s.fg : '#94a3b8'} bg={done ? GREEN : current ? s.bg : '#f1f5f9'} strong={current} />
              {i < RUNNER_PIPELINE.length - 1 && <Typography sx={{ color: '#cbd5e1', fontSize: 12 }}>›</Typography>}
            </Box>
          );
        })}
      </Box>

      {/* Current-stage action card */}
      {!isTerminal && actions.length > 0 && (
        <Box sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#fff', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Current stage: {runnerStageChip(app.status).label}</Typography>
          <TextField label="Note for the record (optional)" ariaLabel="Review note" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} fullWidth multiline sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {actions.map((a) => (
              <Button
                key={a.action}
                ariaLabel={a.label}
                buttonText={a.label}
                variant={a.needs === 'approve' ? 'contained' : 'outlined'}
                size="small"
                sx={a.needs === 'approve' ? undefined : NEUTRAL_BTN}
                disabled={busy}
                // 🛑 Waiving never fires from a single click. It asks for a code
                // first, which is what makes it a deliberate act rather than one
                // more button in a row of pipeline steps.
                onClick={a.action === 'waive_checkr'
                  ? () => setWaiveOpen((v) => !v)
                  : () => void run(a.action, `${a.label} — done.`)}
              />
            ))}
          </Box>

          {waiveOpen && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e8c48a', borderRadius: 2, backgroundColor: '#FDF3E1' }}>
              <Typography sx={{ fontWeight: 700, color: '#7A4A08', mb: 0.5 }}>
                Waive the background check
              </Typography>
              <Typography variant="body2" sx={{ color: '#7A4A08', mb: 1 }}>
                No check will be ordered and no report will ever exist for this applicant. They move straight to banking
                and tax, and their record will say the check was waived rather than cleared — including if they are declined
                later. Paste a waiver code from the Runner applications screen; a code works once and expires.
              </Typography>
              <TextField
                label="Waiver code"
                ariaLabel="Waiver code"
                size="small"
                fullWidth
                value={waiverCode}
                disabled={busy}
                onChange={(e) => setWaiverCode(e.target.value.toUpperCase())}
                sx={{ backgroundColor: '#fff' }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  ariaLabel="Confirm waiving the background check"
                  buttonText={busy ? 'Waiving…' : 'Waive the check'}
                  variant="contained"
                  size="small"
                  disabled={busy || !waiverCode.trim()}
                  onClick={() => void run('waive_checkr', 'Background check waived.')}
                />
                <Button ariaLabel="Cancel" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} disabled={busy} onClick={() => { setWaiveOpen(false); setWaiverCode(''); }} />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch', mb: 2 }}>
        <RunnerInsurancePanel runnerId={app.profile?.id ?? null} onChanged={() => void load()} />
        <RunnerCallPanel applicationId={app.id} applicationStatus={app.status} onChanged={() => void load()} />
      </Box>

      {/* Detail panels */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Panel title="Applicant">
          <Field label="Email" value={app.email} />
          <Field label="Phone" value={app.phoneE164 ?? '—'} />
          <Field label="Date of birth" value={app.dateOfBirth ? fmtDate(app.dateOfBirth) : '—'} />
          <Field label="Metro" value={METRO_LABEL[app.metro] ?? app.metro} />
          <Field label="Zones" value={app.zonesInterested.length ? app.zonesInterested.join(', ') : '—'} />
          <Field label="Transport" value={TRANSPORT_LABEL[app.transportMode] ?? app.transportMode} />
          <Field label="Source" value={app.source} />
          <Field label="Worked with us before?" value={app.priorRelationshipAttested ? `Yes — ${app.priorRelationshipNote ?? 'no detail'}` : 'No'} />
          <Field label="Runner-pool account" value={app.cognitoSub ? 'Provisioned' : 'Not yet provisioned'} />
        </Panel>
        <Panel title="Pipeline record">
          <Field label="Pre-screen decided" value={app.prescreenDecidedAt ? fmtDate(app.prescreenDecidedAt) : '—'} />
          {/* 🛑 A waived applicant has NO Checkr status and NO result, so both
              fields would render an em-dash and read as "not back yet". Say what
              actually happened instead, and who authorised it. */}
          {waived ? (
            <>
              <Field label="Checkr status" value="Waived — no check was ordered" />
              <Field
                label="Waiver"
                value={
                  app.checkrWaiver
                    ? `${app.checkrWaiver.code} · ${WAIVER_REASONS.find((r) => r.value === app.checkrWaiver!.reason)?.label ?? app.checkrWaiver.reason}`
                    : 'recorded'
                }
              />
              <Field label="Authorised by" value={app.checkrWaiver?.mintedBy ?? '—'} />
              <Field label="Applied by" value={app.checkrWaiver?.consumedBy ? `${app.checkrWaiver.consumedBy} · ${fmtDate(app.checkrWaiver.consumedAt)}` : '—'} />
              {app.checkrWaiver?.note && <Field label="Waiver note" value={app.checkrWaiver.note} />}
            </>
          ) : (
            <>
              <Field label="Checkr status" value={checkrStatusLabel(app.checkrStatus)} />
              <Field label="Checkr result" value={app.checkrResult ? `${app.checkrResult} · ${fmtDate(app.checkrCompletedAt)}` : '—'} />
            </>
          )}
          {app.checkrReportUrl && (
            <Field
              label="Checkr report"
              value={
                <a href={app.checkrReportUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8' }}>
                  Open in Checkr dashboard ↗
                </a>
              }
            />
          )}
          <Field label="IDV" value={app.idvStatus ?? '—'} />
          <Field label="Training acked" value={fmtDate(app.trainingAckedAt)} />
          <Field label="Decision" value={app.decidedAt ? `${fmtDate(app.decidedAt)}${app.declineReason ? ` — ${app.declineReason}` : ''}` : '—'} />
          {app.preAdverseSentAt && <Field label="Pre-adverse notice" value={fmtDate(app.preAdverseSentAt)} />}
          {app.profile && <Field label="Runner" value={`${app.profile.glpdId ?? 'no id'} · ${app.profile.lifecycle}`} />}
          {app.profile && (
            <Box sx={{ mt: 1 }}>
              <Button ariaLabel="Capacity limits for this Runner" buttonText="Capacity limits" variant="text" size="small" onClick={() => navigate(`/runner-limits/runners/${app.profile!.id}`)} />
            </Box>
          )}
          {canPurge && app.profile && (
            <Box sx={{ mt: 2, p: 1.5, border: '1px solid #fecaca', backgroundColor: '#fef2f2', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '13px', color: '#b91c1c' }}>Delete this Runner completely</Typography>
              <Typography variant="body2" sx={{ color: '#7f1d1d', fontSize: '12px', mt: 0.5 }}>
                Removes the profile, application, every photo and the login itself, so this person can sign up again from
                scratch. A Runner who has been paid cannot be deleted. Any ban stays in place. Checkr and Stripe records
                are not ours to delete — the ids come back so you can finish there.
              </Typography>
              <TextField
                label="Why are you deleting this Runner?"
                ariaLabel="Reason for deleting this Runner"
                size="small"
                fullWidth
                value={purgeReason}
                disabled={purging}
                onChange={(e) => { setPurgeReason(e.target.value); setPurgeArmed(false); }}
                sx={{ mt: 1, backgroundColor: '#fff' }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {!purgeArmed ? (
                  <Button ariaLabel="Delete this Runner completely" buttonText="Delete Runner…" variant="outlined" size="small"
                    sx={DANGER_BTN} disabled={!purgeReason.trim() || purging} onClick={() => setPurgeArmed(true)} />
                ) : (
                  <>
                    <Button ariaLabel="Confirm permanent deletion" buttonText={purging ? 'Deleting…' : 'Yes — delete permanently'}
                      variant="contained" size="small" sx={DANGER_BTN} disabled={purging}
                      onClick={() => {
                        const profileId = app.profile!.id;
                        setPurging(true);
                        adminApiClient
                          .purgeRunner(profileId, { acknowledge: 'DELETE-THIS-RUNNER-COMPLETELY', reason: purgeReason.trim() })
                          .then((res) => {
                            setFlash({
                              kind: 'success',
                              msg: `Deleted ${res.glpdId ?? 'Runner'}${res.email ? ` (${res.email})` : ''} — ${res.removed.photos} photo(s), login ${res.removed.cognitoAccount ? 'removed' : 'not found'}. Still on Checkr/Stripe: ${[res.retained.checkrCandidateId, res.retained.stripeConnectAccountId, res.retained.stripeIssuingCardholderId].filter(Boolean).join(', ') || 'nothing'}.`,
                            });
                            setPurgeArmed(false);
                            setPurgeReason('');
                          })
                          .catch((e: Error) => setFlash({ kind: 'error', msg: e.message }))
                          .finally(() => setPurging(false));
                      }} />
                    <Button ariaLabel="Cancel deletion" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN}
                      disabled={purging} onClick={() => setPurgeArmed(false)} />
                  </>
                )}
              </Box>
            </Box>
          )}
          {briefs.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>NOTES</Typography>
              {briefs.map((b) => (
                <Typography key={b.id} variant="body2" sx={{ color: 'text.secondary' }}>
                  {fmtDate(b.decidedAt ?? b.generatedAt)} · {b.stage}: {b.brief.note ?? b.brief.summary ?? '—'}
                </Typography>
              ))}
            </Box>
          )}
        </Panel>
      </Box>
    </Box>
  );
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1, py: 0.5 }}>
      <Typography variant="body2" sx={{ color: '#64748b' }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: '#1f2937' }}>{value ?? '—'}</Typography>
    </Box>
  );
}
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ flex: 1, minWidth: 320, p: 2, border: '1px solid #e2e8f0', borderRadius: 2, backgroundColor: '#fff' }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}
